import { addDays, eachDayOfInterval, isSameDay, startOfMonth } from 'date-fns'
import debounce from 'lodash/debounce'
import first from 'lodash/first'
import groupBy from 'lodash/groupBy'
import keyBy from 'lodash/keyBy'
import last from 'lodash/last'
import mapValues from 'lodash/mapValues'
import maxBy from 'lodash/maxBy'
import minBy from 'lodash/minBy'
import pick from 'lodash/pick'
import { action, comparer, computed, makeObservable, observable, reaction, when } from 'mobx'
import { getValue, isPending } from 'mobx-async'
import { computedFn } from 'mobx-utils'

import { BookingIssue, UUID } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { Alert, Awaited } from '@avantstay/backoffice-core'
import { SortDirection } from '@avantstay/data-grid'
import { Defined } from '@avantstay/graphql-ts-client'

import { getBlockedDatesById } from '@new/__global/components/FastTapeChart/__utils'
import { ExtendedColumnKey } from '@new/__global/components/FiltersFramework'
import { sortOrderMappings } from '@new/__global/components/FiltersFramework/_utils'
import { PeriodKind } from '@new/__global/components/TapeChart/TapeChart.types'
import { getTapeChartDataStatus } from '@new/__global/components/TapeChartDataError/getTapeChartDataStatus'
import { TapeChartDataStatus } from '@new/__global/components/TapeChartDataError/types'
import { HomeBasicView } from '@new/domains/bookingsTapeChart/components/CreateBookingForm/CreateBookingForm.types'
import { UseSelectRangeProps } from '@new/domains/bookingsTapeChart/hooks/useSelectRange'
import { BookingsTapeChartStore } from '@new/domains/bookingsTapeChart/stores/BookingsTapeChartStore'
import {
  AvailableDatesRange,
  BlockedPeriodType,
  DayIndexes,
  RowProps,
  SortOrderProps,
  TapeType,
} from '@new/domains/fastBookingsTapeChart/__types'
import { UNKNOWN_REGION } from '@new/domains/fastBookingsTapeChart/config/contants'
import {
  BookingFilterIds,
  CustomFilterIds,
  fastBookingsTapeChartFiltersStore,
  HomeFilterIds,
} from '@new/domains/fastBookingsTapeChart/internal'
import { scrollController } from '@new/domains/fastBookingsTapeChart/ScrollController'
import { tapesStore } from '@new/domains/fastBookingsTapeChart/stores/TapesStore'
import { PropertyInfoStore } from '@new/domains/properties/stores/PropertyInfoStore'

import { ROUTES } from '@routes'
import { computedRouteParam } from '@stores/computedRouteParam'
import { SidePanelStore, SidePanelType } from '@stores/SidePanelStore'
import { trackError } from '@tracking/customEventReport'

import { getColumnOffsets, parseDate, shortISODate } from '../__utils'
import { arrangeBlocks } from './arrangeBlocks'
import { arriere } from './arriere'
import { mergeOverlappingBlocks } from './mergeOverlappingBlocks'

type ApiBlockedPeriods = ReturnType<FastBookingsTapeChartStore['api']['fetchBlockedPeriods']>
type ApiHomes = Awaited<
  ReturnType<FastBookingsTapeChartStore['api']['fetchTapechartHomes']>
>['homesList']
export type AvailabilityBlock = Defined<Awaited<ApiBlockedPeriods>>[0]
export type ParentBlock = Defined<AvailabilityBlock['parent']>
export type Home = FastBookingsTapeChartStore['homes'][0]
export type HomeExtendedInfo = Defined<FastBookingsTapeChartStore['homesExtendedInfo']>[0]
export type HomeBlocks = ReturnType<FastBookingsTapeChartStore['getHomeBlocks']>
export type BookingBlock = NonNullable<HomeBlocks['bookings']>[0]
export type BlockedPeriod = NonNullable<HomeBlocks['blockedPeriods']>[0]
export type BlockedBlock = BlockedPeriod['blocks'][0]
export type HomeRates = ReturnType<FastBookingsTapeChartStore['getHomeRates']>

const resolveWithAnimationFrame = <T>(thing: T) =>
  new Promise<T>(resolve => requestAnimationFrame(() => resolve(thing)))

const HOMES_EMPTY_ARRAY: ApiHomes = []
const EMPTY_SELECT_RANGE_PROPS: UseSelectRangeProps = {
  blockedDatesById: {},
  blockedDatesWithSingleNight: {},
  datePrices: [],
}

export class FastBookingsTapeChartStore {
  api = arriere

  pageSize = 30

  refreshBlockedPeriodsCount = 0

  refreshRatesCount = 0

  refreshBookingsCount = 0

  refreshHomesCount = 0

  forceLoadingOverlay = false

  hasInitLoaded = false

  // Sticky per-source "has failed at least once for the current data" flags,
  // set from the blocks/bookings fetch outcome (see __ASYNC_blocks/__ASYNC_bookings).
  // They keep bookings/blocks in the first-load overlay while healthy, then drop
  // them out once a fetch errors so retries surface the banner instead of the overlay.
  // Driving them off the fetch outcome (not a constructor reaction over *Status) is
  // what keeps the data pipeline from being triggered at store construction.
  blocksHasErrored = false

  bookingsHasErrored = false

  private _searchTerms = computedRouteParam<string>('t', { patterns: [ROUTES.fastTapeChart.path] })

  private _sortBy = computedRouteParam<ExtendedColumnKey | undefined>('sortBy', {
    patterns: [ROUTES.fastTapeChart.path],
  })

  private _sortOrder = computedRouteParam<SortDirection>('sortOrder', {
    patterns: [ROUTES.fastTapeChart.path],
  })

  private _page = computedRouteParam.int('page', {
    patterns: [ROUTES.fastTapeChart.path],
    defaultValue: 1,
  })

  constructor() {
    makeObservable<
      FastBookingsTapeChartStore,
      | '__ASYNC_blocks'
      | '__ASYNC_blocksByHome'
      | '__ASYNC_bookings'
      | '__ASYNC_bookingsByHome'
      | 'rangeFilters'
      | '__ASYNC_home_results'
      | '__ASYNC_home_extended_results'
      | '__ASYNC_rates'
      | 'blocks'
      | 'bookingsByHome'
      | 'rawHomeResults'
      | 'rawHomeExtendedResults'
      | 'rates'
      | 'allBookings'
      | 'mergedHomeBlocks'
      | 'setBookingsHasErrored'
      | 'setBlocksHasErrored'
    >(this, {
      pageSize: observable,
      refreshBlockedPeriodsCount: observable,
      refreshRatesCount: observable,
      refreshBookingsCount: observable,
      refreshHomesCount: observable,
      forceLoadingOverlay: observable,
      hasInitLoaded: observable,
      blocksHasErrored: observable,
      bookingsHasErrored: observable,
      checkInitialUrlParams: action.bound,
      goToPropertyPage: action.bound,
      resetPage: action.bound,
      searchTerms: computed,
      page: computed,
      pagesCount: computed,
      setSortOrder: action.bound,
      setPage: action.bound,
      visibleMonths: computed,
      formattedVisibleDates: computed,
      grid: computed,
      __ASYNC_blocks: computed,
      __ASYNC_blocksByHome: computed,
      __ASYNC_bookings: computed,
      __ASYNC_bookingsByHome: computed,
      rangeFilters: computed,
      homeFilters: computed({ equals: comparer.structural }),
      bookingFilters: computed({ equals: comparer.structural }),
      __ASYNC_home_results: computed,
      __ASYNC_home_extended_results: computed,
      __ASYNC_rates: computed,
      blocks: computed,
      bookingsByHome: computed,
      rawHomeResults: computed,
      rawHomeExtendedResults: computed,
      totalHomes: computed,
      rates: computed,
      isLoadingBookings: computed,
      isLoading: computed,
      isLoadingRates: computed,
      blocksStatus: computed,
      ratesStatus: computed,
      bookingsStatus: computed,
      allBookings: computed,
      mergedHomeBlocks: computed,
      selectRangeProps: computed,
      relatedBlocks: computed,
      homes: computed,
      allHomes: computed,
      homesExtendedInfo: computed,
      homesSortedRawResult: computed,
      homesCountByRegion: computed,
      homeIds: computed,
      visibleDates: computed,
      bookingIssues: computed,
      availableDatesFilterIndexes: computed,
      selectProperty: action.bound,
      showPropertyInfo: action.bound,
      setBookingsHasErrored: action.bound,
      setBlocksHasErrored: action.bound,
      reloadHomes: action.bound,
      reloadBookings: action.bound,
      reloadBlockedPeriods: action.bound,
      reloadRates: action.bound,
      refreshData: action.bound,
      setAddressFilter: action.bound,
    })

    reaction(
      () => this.homeFilters,
      () => this.resetPage(),
    )
    reaction(
      () => this.setSortOrder,
      () => this.resetPage(),
    )
    reaction(
      () => this._searchTerms.get(),
      () => this.resetPage(),
    )
  }

  async checkInitialUrlParams() {
    const propertyIdParam = computedRouteParam<string | undefined>('property-id', {
      patterns: [ROUTES.fastTapeChart.path],
    })
    const propertyId = propertyIdParam.get()

    const bookingIdParam = computedRouteParam<string | undefined>('booking-id', {
      patterns: [ROUTES.fastTapeChart.path],
    })
    const bookingId = bookingIdParam.get()

    const blockIdParam = computedRouteParam<string | undefined>('block-id', {
      patterns: [ROUTES.fastTapeChart.path],
    })
    const blockId = blockIdParam.get()

    const selectedArrivalChecklistHomeId = computedRouteParam<string | undefined>(
      'selectedArrivalChecklistHomeId',
      {
        patterns: [ROUTES.fastTapeChart.path],
      },
    ).get()

    if (propertyId) {
      await this.goToPropertyPage(propertyId)
      if (propertyIdParam.get() !== propertyId) return

      if (!selectedArrivalChecklistHomeId && !blockId && !bookingId) {
        this.showPropertyInfo(propertyId)
      }
    }

    if (bookingId) {
      await this.waitForBookings()
      if (bookingIdParam.get() !== bookingId) return

      tapesStore.selectionSourceTape = {
        ids: bookingId,
        type: TapeType.Booking,
      }
      tapesStore.showTapeSidePanel()
    }

    if (blockId) {
      await this.waitForBookings()
      if (blockIdParam.get() !== blockId) return

      tapesStore.selectionSourceTape = {
        ids: blockId,
        type: TapeType.BlockedPeriod,
      }
      tapesStore.showTapeSidePanel()
    }
  }

  waitForBookings() {
    return when(() => getValue(this.__ASYNC_bookings) !== undefined)
  }

  waitForHomes() {
    return when(() => getValue(this.__ASYNC_home_results) !== undefined)
  }

  async goToPropertyPage(homeId: string) {
    await this.waitForHomes()

    const isPropertyOnCurrentPage = this.homes.some(home => home.id === homeId)
    if (!isPropertyOnCurrentPage) {
      this.api.fetchTapechartHomeIds.clear()
      this.api.fetchTapechartHomes.clear()
      const { homesList } = await this.api.fetchTapechartHomeIds({
        filters: this.homeFilters,
      })
      const propertyIndex = homesList.findIndex(home => home.id === homeId)
      if (propertyIndex === -1) {
        return
      }

      const propertyPage = Math.floor(propertyIndex / this.pageSize) + 1
      if (this._page.get() !== propertyPage) {
        await this.setPage(propertyPage)
      } else {
        this.reloadHomes()
      }
    }

    await this.waitForBookings()
    const rowIndex = this.getHomeIndex(homeId)
    if (rowIndex !== -1) {
      await scrollController.scrollToRow(rowIndex)
    }
  }

  resetPage() {
    if (this.page > 1) {
      this.setPage(1)
    }
  }

  sortBy = computedFn(() => {
    return this._sortBy.get()
  })

  sortOrder = computedFn(() => {
    return this._sortOrder.get()
  })

  get searchTerms() {
    return this._searchTerms.get()
  }

  get page() {
    return this._page.get()
  }

  get pagesCount() {
    return Math.ceil((this.rawHomeResults?.total ?? 0) / this.pageSize)
  }

  setSortOrder({ sortOrder, sortByKey }: SortOrderProps) {
    const newSortByKey = sortOrder || sortOrder === SortDirection.None ? sortByKey : undefined

    this._sortBy.push(newSortByKey as ExtendedColumnKey)
    this._sortOrder.push(sortOrder)
  }

  setPage(page: number) {
    return this._page.push(page)
  }

  setSearchTerms = debounce((v: string) => {
    requestAnimationFrame(() => {
      this.resetPage()
      this._searchTerms.push(v)
    })
  }, 400)

  get visibleMonths() {
    return Object.entries(groupBy(this.visibleDates, it => startOfMonth(it).toISOString())).map(
      ([month, days]) => ({
        month,
        days,
      }),
    )
  }

  private getHomeOccupiedDates = computedFn((homeId: string) => {
    const homeBlocks = this.mergedHomeBlocks[homeId]
    const homeBookings = this.getHomeBookings(homeId)

    const editableBlocksDays = (homeBlocks?.editableBlocks ?? []).flatMap(block =>
      eachDayOfInterval({
        start: parseDate(block.period.start),
        end: parseDate(block.period.end),
      }).slice(0, -1),
    )

    const readOnlyBlocksDays = (homeBlocks?.readOnlyBlocks ?? []).flatMap(block =>
      eachDayOfInterval({
        start: parseDate(block.period.start),
        end: parseDate(block.period.end),
      }).slice(0, -1),
    )

    const bookingsDays =
      homeBookings?.flatMap(booking =>
        eachDayOfInterval({
          start: parseDate(booking.checkInTiming.effectiveTime),
          end: parseDate(booking.checkOutTiming.effectiveTime),
        }).slice(0, -1),
      ) ?? []

    return new Set(
      [...editableBlocksDays, ...readOnlyBlocksDays, ...bookingsDays].map(it => shortISODate(it)),
    )
  })

  get formattedVisibleDates() {
    return this.visibleDates.map(shortISODate)
  }

  get grid() {
    return {
      start: this.formattedVisibleDates[0],
      end: this.formattedVisibleDates[this.formattedVisibleDates.length - 1],
    }
  }

  private get __ASYNC_blocks() {
    void [this.refreshBlockedPeriodsCount]
    return this.homeIds.length
      ? this.api
          .fetchBlockedPeriods({
            startDate: shortISODate(first(this.visibleDates)),
            endDate: shortISODate(last(this.visibleDates)),
            homeIds: this.homeIds,
          })
          .then(result => {
            this.setBlocksHasErrored(false)
            return result
          })
          .catch(error => {
            this.setBlocksHasErrored(true)
            trackError('Failed attempt to fetch tape chart blocks', { error })
            throw error
          })
      : Promise.resolve(undefined)
  }

  private get __ASYNC_blocksByHome() {
    return this.__ASYNC_blocks
      .then(bookings => groupBy(bookings, it => it.homeId))
      .then(resolveWithAnimationFrame)
  }

  private get __ASYNC_bookings() {
    void [this.refreshBookingsCount]
    return this.homeIds.length
      ? this.api
          .fetchBookings({
            startDate: shortISODate(addDays(parseDate(first(this.visibleDates)), -1)),
            endDate: shortISODate(last(this.visibleDates)),
            homeIds: this.homeIds,
          })
          .then(result => {
            this.setBookingsHasErrored(false)
            return result
          })
          .catch(error => {
            this.setBookingsHasErrored(true)
            trackError('Failed attempt to fetch tape chart bookings', { error })
            throw error
          })
      : Promise.resolve(undefined)
  }

  private get __ASYNC_bookingsByHome() {
    void [this.refreshBookingsCount]
    return this.__ASYNC_bookings
      .then(bookings => groupBy(bookings, it => it.homeId))
      .then(resolveWithAnimationFrame)
  }

  private get rangeFilters() {
    const rangeFiltersIds = [
      HomeFilterIds.Guests,
      HomeFilterIds.PriceRange,
      HomeFilterIds.BedroomsRange,
      HomeFilterIds.BathroomsRange,
      HomeFilterIds.AddressRadius,
      HomeFilterIds.CheckInDates,
    ]

    // AvailableDates filter value needs to be destructed to match API query structure
    const availableDatesFilter: AvailableDatesRange | undefined =
      fastBookingsTapeChartFiltersStore.filteringArgs[CustomFilterIds.AvailableDates]
    const checkInDatesFilter: AvailableDatesRange | undefined =
      fastBookingsTapeChartFiltersStore.filteringArgs[HomeFilterIds.CheckInDates]
    const guestsRangeFilter = fastBookingsTapeChartFiltersStore.filteringArgs[HomeFilterIds.Guests]
    const priceRangeFilter =
      fastBookingsTapeChartFiltersStore.filteringArgs[HomeFilterIds.PriceRange]
    const bedroomsRangeFilter =
      fastBookingsTapeChartFiltersStore.filteringArgs[HomeFilterIds.BedroomsRange]
    const bathroomsRangeFilter =
      fastBookingsTapeChartFiltersStore.filteringArgs[HomeFilterIds.BathroomsRange]

    const addressRadiusFilter =
      fastBookingsTapeChartFiltersStore.filteringArgs[HomeFilterIds.AddressRadius]

    const guestsFilter = {
      minOccupancy: guestsRangeFilter?.from,
      maxOccupancy: guestsRangeFilter?.to,
    }

    const pricesFilter = {
      minPrice: priceRangeFilter?.minPrice,
      maxPrice: priceRangeFilter?.maxPrice,
    }

    const bedroomsFilter = {
      minBedrooms: bedroomsRangeFilter?.from,
      maxBedrooms: bedroomsRangeFilter?.to,
    }

    const bathroomsFilter = {
      minBathrooms: bathroomsRangeFilter?.from,
      maxBathrooms: bathroomsRangeFilter?.to,
    }

    const pointOfOrigin = {
      pointOfOrigin: {
        latitude: addressRadiusFilter?.latitude,
        longitude: addressRadiusFilter?.longitude,
      },
    }

    const address =
      addressRadiusFilter?.radius && addressRadiusFilter?.latitude && addressRadiusFilter?.longitude
        ? {
            addressRadius: {
              radius: addressRadiusFilter?.radius,
              ...pointOfOrigin,
            },
          }
        : undefined

    const additionalRangeFilters = {
      ...guestsFilter,
      ...pricesFilter,
      ...bedroomsFilter,
      ...bathroomsFilter,
      ...address,
    }

    const checkInExistsFor = checkInDatesFilter?.checkIn
      ? {
          checkInExistsFor: {
            start: checkInDatesFilter.checkIn,
            end: checkInDatesFilter.checkOut,
          },
        }
      : {}

    return {
      rangeFiltersIds,
      availableDatesFilter,
      checkInExistsFor,
      additionalRangeFilters,
    }
  }

  get homeFilters() {
    const { rangeFiltersIds, availableDatesFilter, additionalRangeFilters, checkInExistsFor } =
      this.rangeFilters

    const filters = pick(
      fastBookingsTapeChartFiltersStore.filteringArgs,
      Object.values(HomeFilterIds).filter(filter => !rangeFiltersIds.includes(filter)),
    )

    if (!availableDatesFilter) {
      return { ...filters, ...additionalRangeFilters, ...checkInExistsFor }
    }

    return {
      ...filters,
      ...availableDatesFilter,
      ...additionalRangeFilters,
      ...checkInExistsFor,
    }
  }

  get bookingFilters() {
    return pick(fastBookingsTapeChartFiltersStore.filteringArgs, Object.values(BookingFilterIds))
  }

  private get __ASYNC_home_results() {
    void [this.refreshHomesCount]

    if (!fastBookingsTapeChartFiltersStore.isTapeChartActive) return Promise.resolve(undefined)

    return this.api
      .fetchHomes({
        terms: this._searchTerms.get(),
        page: this.page,
        pageSize: this.pageSize,
        filters: this.homeFilters,
        sortBy: this.sortBy(),
        sortOrder: sortOrderMappings[this.sortOrder()],
      })
      .catch(error => {
        Alert.Error(error.message)
        throw error
      })
  }

  private get __ASYNC_home_extended_results() {
    void [this.refreshHomesCount]

    if (this.homeIds.length) {
      return this.api
        .fetchHomesListExtendedInfo({
          homeIds: this.homeIds,
        })
        .catch(error => {
          Alert.Error(error.message)
          throw error
        })
    } else {
      return Promise.resolve([])
    }
  }

  private get __ASYNC_rates() {
    void [this.refreshRatesCount]

    const fromDate = first(this.visibleDates) ?? ''
    const toDate = shortISODate(addDays(parseDate(last(this.visibleDates)) ?? 0, 1))
    const homeIds = this.homes?.map(home => home.id)

    return this.homeIds.length
      ? this.api.fetchRates({
          startDate: shortISODate(fromDate),
          endDate: toDate,
          homeIds,
        })
      : Promise.resolve(undefined)
  }

  private get blocks() {
    return getValue(this.__ASYNC_blocksByHome)
  }

  private get bookingsByHome() {
    return getValue(this.__ASYNC_bookingsByHome)
  }

  private get rawHomeResults() {
    const homeResults = getValue(this.__ASYNC_home_results)
    const neededPageItemsToFull = this.pageSize * this.page
    const pageCount = Math.ceil((homeResults?.total ?? 0) / this.pageSize)
    const isLastPage = this.page === pageCount
    const shouldResetPage =
      !isLastPage && homeResults && neededPageItemsToFull > (homeResults?.total ?? 0)

    /**
     * Below logic is for deep-linking only
     *  We call it after homes have been loaded to avoid multiple calls to homes and rates
     *  */
    if (homeResults && !this.hasInitLoaded) {
      this.checkInitialUrlParams()
      this.hasInitLoaded = true
    }

    if (shouldResetPage) {
      this.resetPage()
    }

    return homeResults
  }

  private get rawHomeExtendedResults() {
    return getValue(this.__ASYNC_home_extended_results)
  }

  get totalHomes() {
    return this.rawHomeResults?.total ?? 0
  }

  private get rates() {
    return getValue(this.__ASYNC_rates)
  }

  get isLoadingBookings() {
    return isPending(this.__ASYNC_bookings)
  }

  get isLoading() {
    if (!fastBookingsTapeChartFiltersStore.isTapeChartActive) return false

    // Bookings and blocks drive the full-screen overlay only until they first
    // resolve or fail. Once a source has errored, its retries are surfaced by
    // the error banner instead, so a ~30s failed query doesn't blank the chart.
    return (
      this.forceLoadingOverlay ||
      isPending(this.__ASYNC_home_results) ||
      (isPending(this.__ASYNC_bookings) && !this.bookingsHasErrored) ||
      (isPending(this.__ASYNC_blocks) && !this.blocksHasErrored)
    )
  }

  get isLoadingRates() {
    return isPending(this.__ASYNC_rates)
  }

  get blocksStatus(): TapeChartDataStatus {
    return getTapeChartDataStatus(this.__ASYNC_blocks)
  }

  get ratesStatus(): TapeChartDataStatus {
    return getTapeChartDataStatus(this.__ASYNC_rates)
  }

  get bookingsStatus(): TapeChartDataStatus {
    return getTapeChartDataStatus(this.__ASYNC_bookings)
  }

  private get allBookings() {
    return mapValues(this.bookingsByHome, bookings =>
      bookings.map(booking => ({
        type: 'booking',
        ...booking,
        ...getColumnOffsets({
          startDate: booking.checkInTiming.effectiveTime,
          endDate: booking.checkOutTiming.effectiveTime,
          visibleDates: this.visibleDates,
        }),
      })),
    )
  }

  private get mergedHomeBlocks() {
    const $dayBeforeStartDate = addDays(fastBookingsTapeChartFiltersStore.startDate, -1)
    const $lastVisibleDate = addDays(parseDate(last(this.visibleDates)), 1)

    const mergeBlocks = (blocks: AvailabilityBlock[]) =>
      mergeOverlappingBlocks(blocks, {
        getStartDate: block =>
          shortISODate(maxBy([$dayBeforeStartDate, parseDate(block.period.start)], d => +d) ?? 0),
        getEndDate: block =>
          shortISODate(minBy([$lastVisibleDate, parseDate(block.period.end)], d => +d) ?? 0),
      }).map(groupOfBlocks => ({
        id: Array.from(groupOfBlocks.blocks)
          .map(b => b.id)
          .join(','),
        blocks: Array.from(groupOfBlocks.blocks),
        period: groupOfBlocks.period,
        ...getColumnOffsets({
          startDate: groupOfBlocks.period.start ?? '',
          endDate: groupOfBlocks.period.end ?? '',
          visibleDates: this.visibleDates,
        }),
      }))

    return mapValues(this.blocks, homeBlocks => {
      const { editable: editableBlocks, readOnly: readOnlyBlocks } = groupBy(homeBlocks, block =>
        !block.parent && block.source !== 'BUYOUT' ? 'editable' : 'readOnly',
      )

      return {
        editableBlocks: mergeBlocks(editableBlocks).map(it => ({
          ...it,
          type: BlockedPeriodType.EditableBlock,
        })),
        readOnlyBlocks: mergeBlocks(readOnlyBlocks).map(it => ({
          ...it,
          type: BlockedPeriodType.ReadOnlyBlock,
        })),
      }
    })
  }

  get selectRangeProps(): UseSelectRangeProps {
    const blocks = getValue(this.__ASYNC_blocks)
    const bookings = getValue(this.__ASYNC_bookings)
    if (!blocks || !bookings) {
      // to not calculate it twice, we calculate
      // only when both are loaded
      return EMPTY_SELECT_RANGE_PROPS
    }
    const mappedBlocks = blocks.map(block => ({
      id: block.id,
      kind: PeriodKind.block,
      homeId: block.homeId,
      startDate: block.period.start,
      endDate: block.period.end,
    }))
    const mappedBookings = bookings.map(booking => ({
      id: booking.id,
      kind: PeriodKind.booking,
      homeId: booking.homeId,
      startDate: booking.checkInTiming.effectiveTime,
      endDate: booking.checkOutTiming.effectiveTime,
    }))

    const allPeriods = [...mappedBlocks, ...mappedBookings]

    return {
      blockedDatesById: getBlockedDatesById(allPeriods),
      blockedDatesWithSingleNight: getBlockedDatesById(allPeriods, true),
      datePrices: Object.values(this.rates ?? []).flat(),
    }
  }

  get relatedBlocks() {
    if (this.blocks) {
      return Object.values(this.blocks).flatMap(blocksObject =>
        blocksObject?.map(blockTape => ({
          id: blockTape?.id,
          relatedIds: [blockTape.parent?.id, blockTape.parent?.bookingId],
        })),
      )
    }
    return []
  }

  get homes() {
    return this.rawHomeResults?.results ?? HOMES_EMPTY_ARRAY
  }

  get allHomes() {
    return this.rawHomeResults?.homesList ?? HOMES_EMPTY_ARRAY
  }

  get homesExtendedInfo() {
    const allHomes = this.homes?.filter(home => this.homeIds?.includes(home.id))
    return (
      this.rawHomeExtendedResults?.map(extendedHome => ({
        id: extendedHome.id,
        ...extendedHome.home,
        ...allHomes.find(home => extendedHome.id === home.id)?.home,
      })) ?? HOMES_EMPTY_ARRAY
    )
  }

  get homesSortedRawResult() {
    return this.rawHomeResults?.sortedRawResults ?? HOMES_EMPTY_ARRAY
  }

  get homesCountByRegion() {
    return this.homesSortedRawResult.reduce<{ [regionId: UUID]: number }>((acc, home) => {
      const regionId = home.region?.id ?? UNKNOWN_REGION
      acc[regionId] = (acc[regionId] ?? 0) + 1
      return acc
    }, {})
  }

  get homeIds() {
    return this.homes.map(home => home.id)
  }

  get visibleDates() {
    return eachDayOfInterval({
      start: fastBookingsTapeChartFiltersStore.startDate as Date,
      end: fastBookingsTapeChartFiltersStore.endDate as Date,
    })
  }

  get bookingIssues(): Record<Partial<BookingIssue>, number> {
    const issueCounts = {} as Record<Partial<BookingIssue>, number>

    if (!this.bookingsByHome) {
      return issueCounts
    }
    Object.values(this.bookingsByHome).forEach(homeBookings =>
      homeBookings.forEach(({ issues }) => {
        issues.forEach(issue => {
          issueCounts[issue] = (issueCounts[issue] || 0) + 1
        })
      }),
    )
    return issueCounts
  }

  get availableDatesFilterIndexes(): DayIndexes | undefined {
    const { availableDates } = fastBookingsTapeChartFiltersStore
    if (!availableDates) {
      return undefined
    }

    const getIndex = (date: Date) =>
      this.visibleDates.findIndex(visibleDate => {
        return isSameDay(visibleDate, date)
      })

    const startDayIndex = getIndex(availableDates.start)
    const endDayIndex = getIndex(availableDates.end) + 1

    if (startDayIndex === -1 || endDayIndex === 0) {
      return undefined
    }

    return {
      startDayIndex,
      endDayIndex,
    }
  }

  getHomeRates = computedFn((homeId: string) => {
    const blockedDates = this.getHomeOccupiedDates(homeId)

    return keyBy(
      this.rates?.[homeId]?.map(rate => ({
        ...rate,
        occupied: blockedDates.has(rate.date),
      })),
      it => it.date,
    )
  })

  getHomeBlocks = computedFn((homeId: string, { skipArranging }: { skipArranging: boolean }) => {
    const bookings = this.getHomeBookings(homeId) ?? []
    const { editableBlocks = [], readOnlyBlocks = [] } = this.mergedHomeBlocks[homeId] ?? {}

    if (skipArranging) {
      return {}
    }

    const { bookings: _bookings = [], blockedPeriods = [] } = groupBy(
      arrangeBlocks([...bookings, ...editableBlocks, ...readOnlyBlocks]),
      it => (it.type === 'booking' ? 'bookings' : 'blockedPeriods'),
    )

    return {
      bookings: _bookings as unknown as Array<(typeof bookings)[0] & RowProps>,
      blockedPeriods: blockedPeriods as unknown as Array<
        ((typeof editableBlocks)[0] | (typeof readOnlyBlocks)[0]) & RowProps
      >,
    }
  })

  getHomeBookings = computedFn((homeId: string) => {
    return this.allBookings?.[homeId]
  })

  getBookingById = computedFn((bookingId: string) => {
    return getValue(this.__ASYNC_bookings)?.find(({ id }) => id === bookingId)
  })

  getBlockById = computedFn((blockId: string) => {
    return getValue(this.__ASYNC_blocks)?.find(({ id }) => id === blockId)
  })

  getHomeIndex = computedFn((homeId: UUID) => {
    return this.homes.findIndex(({ id }) => id === homeId)
  })

  getHomeInfoObject = computedFn((homeId: UUID): HomeBasicView => {
    const home = this.allHomes.find(({ id }) => id === homeId)
    if (!home) {
      throw Error('incorrect home id')
    }
    return {
      id: homeId,
      name: home.name,
      regionName: home.region?.name ?? UNKNOWN_REGION,
      hotelRoomTypeId: home.home.hotelRoomType?.hotelRoomTypeId,
    }
  })

  selectProperty(propertyId: UUID) {
    PropertyInfoStore.setCurrentHomeId(propertyId)
    BookingsTapeChartStore.setPropertyIdParam(propertyId)
  }

  showPropertyInfo(propertyId: UUID) {
    this.selectProperty(propertyId)
    SidePanelStore.setCurrentSidePanel(SidePanelType.tapeChartPropertyInfo, {
      screenHasSecondaryMenu: true,
    })
  }

  private setBookingsHasErrored(hasErrored: boolean) {
    this.bookingsHasErrored = hasErrored
  }

  private setBlocksHasErrored(hasErrored: boolean) {
    this.blocksHasErrored = hasErrored
  }

  reloadHomes() {
    this.api.fetchTapechartHomeIds.clear()
    this.api.fetchTapechartHomes.clear()
    this.api.fetchHomesListExtendedInfo.clear()
    this.refreshHomesCount += 1
  }

  reloadBookings() {
    this.api.fetchBookings.clear()
    this.refreshBookingsCount += 1
  }

  reloadBlockedPeriods() {
    this.api.fetchBlockedPeriods.clear()
    this.refreshBlockedPeriodsCount += 1
  }

  reloadRates() {
    this.api.fetchRates.clear()
    this.refreshRatesCount += 1
  }

  refreshData() {
    this.reloadHomes()
    this.reloadBookings()
    this.reloadBlockedPeriods()
    this.reloadRates()
  }

  setAddressFilter(value?: string) {
    fastBookingsTapeChartFiltersStore.setAddressFilter(value)
  }
}

export const fastBookingsTapeChartStore = new FastBookingsTapeChartStore()
